"use client";

import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Label } from "semantic-ui-react";
import ButtonItem from "../../../components/Button";
import { fetchUserAttributes } from "aws-amplify/auth";
import { editorData } from "./data.json";

/**
 * This component handles:
 * 1. Regular users: Redirects to /NHReview page to write a review
 * 2. Editor users: Redirects to /NHReview?role=editor page
 * 3. Non-logged in users: Triggers sign-up modal and stores return URL
 *
 * @param {Object} props
 * @param {string} props.neighborhoodId - The ID of the neighborhood
 * @param {Array} props.nhdetails - Neighborhood details array
 */

const ShareCommentsButton = ({ neighborhoodId, nhdetails }) => {
  const router = useRouter();
  const [isEditor, setIsEditor] = useState(false);
  const [loggedin, setLoggedin] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if user is logged in and if they're an editor
    const checkUserStatus = async () => {
      try {
        const attributes = await fetchUserAttributes();
        const userEmail = attributes.email;

        // User is logged in
        setLoggedin(true);

        // Check if user is an editor
        if (userEmail && editorData.includes(userEmail)) {
          setIsEditor(true);
        }
      } catch (error) {
        // User is not logged in
        setLoggedin(false);
        setIsEditor(false);
      }
    };

    // Check mobile status
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkUserStatus();
    checkMobile();

    // Add resize listener for mobile detection
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  /**
   * Handle click for editor users
   * Saves neighborhood data to localStorage and redirects to editor review page
   */
  const handleEditorReviewClick = () => {
    // Store neighborhood details in localStorage for use in review page
    if (nhdetails && nhdetails.length > 0) {
      localStorage.setItem("nhdetails", JSON.stringify(nhdetails));
    }

    // Store neighborhood ID
    localStorage.setItem(
      "neighborhoodId",
      neighborhoodId || "60e879f2-b1b0-4ce9-bb26-f06bb9631e98",
    );

    if (loggedin) {
      // Remove any existing saveR flag
      localStorage.removeItem("saveR");

      // Redirect to editor review page
      router.push("/NHReview?role=editor");

      // Scroll to top after navigation
      if (typeof window !== "undefined") {
        window.scrollTo(0, 0);
      }
    } else {
      // Store flag to indicate user wants to save a review
      localStorage.setItem("saveR", "saveReview");

      // Store current URL to return after login
      if (typeof window !== "undefined") {
        localStorage.setItem("previous_url", window.location.href);
      }

      // Trigger sign-up modal
      const signUpBtn = document.getElementById("sign_up_btn");
      if (signUpBtn) {
        signUpBtn.click();
      }
    }
  };

  /**
   * Handle click for regular users
   * Saves neighborhood data to localStorage and redirects to review page
   */
  const handleReviewClick = () => {
    // Store neighborhood details in localStorage for use in review page
    if (nhdetails && nhdetails.length > 0) {
      localStorage.setItem("nhdetails", JSON.stringify(nhdetails));
    }

    // Store neighborhood ID
    localStorage.setItem(
      "neighborhoodId",
      neighborhoodId || "60e879f2-b1b0-4ce9-bb26-f06bb9631e98",
    );

    // Remove any existing saveR flag
    localStorage.removeItem("saveR");

    // Redirect to review page
    router.push("/NHReview");

    // Scroll to top after navigation
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }

    // Store return URL if user is not logged in
    if (!loggedin && typeof window !== "undefined") {
      localStorage.setItem("previous_url", window.location.href);
    }
  };

  /**
   * Main click handler - delegates to editor or regular handler based on user role
   */
  const handleClick = () => {
    if (isEditor) {
      handleEditorReviewClick();
    } else {
      handleReviewClick();
    }
  };

  return (
    <span>
      <ButtonItem
        as="div"
        labelPosition="right"
        floated="right"
        onClick={handleClick}
        className="theme_blue btn_blue"
      >
        <ButtonItem icon="write" className="btn_blue_bg" />
        <Label
          as="a"
          name="saveNeighbor"
          content={isMobile ? "Share Comments" : "Share Comments"}
          basic
          pointing="left"
          className="theme_blue btn_blue"
        />
      </ButtonItem>
    </span>
  );
};

export default ShareCommentsButton;
